import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLang, track } from "@/context/LanguageContext";
import { SectionLabel } from "@/components/Editorial";
import { PageHead } from "@/components/Shared";
import { BsportWidget } from "@/components/BsportWidget";
import { BSPORT } from "@/data/config";
import { API } from "@/lib/api";

const calendarConfig = {
  calendar: {
    coaches: [],
    establishments: [],
    metaActivities: [],
    levels: [],
    variant: null,
    groupSessionByPeriod: true,
  },
};

const DAY_START = 7 * 60;
const DAY_END = 21 * 60;
const PX_PER_MIN = 56 / 60;

const WCOPY = {
  en: {
    week: "Week", list: "List", today: "Today",
    thisWeek: "This week", allActivities: "All classes", allCoaches: "All instructors", allLevels: "All levels", location: "Maison Core — Buitenveldert",
    days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    full: "Full", available: "Available", left: (n) => `${n} left`,
    empty: "No classes match these filters this week.",
    loading: "Loading the week…",
    error: "The schedule could not be loaded.", openBsport: "Open the schedule →",
    level: (n) => `Level ${n}`,
  },
  nl: {
    week: "Week", list: "Lijst", today: "Vandaag",
    thisWeek: "Deze week", allActivities: "Alle lessen", allCoaches: "Alle instructors", allLevels: "Alle niveaus", location: "Maison Core — Buitenveldert",
    days: ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"],
    full: "Vol", available: "Beschikbaar", left: (n) => `${n} plekken`,
    empty: "Geen lessen die aan deze filters voldoen deze week.",
    loading: "Week wordt geladen…",
    error: "Het rooster kon niet worden geladen.", openBsport: "Open het rooster →",
    level: (n) => `Niveau ${n}`,
  },
};

const mondayOf = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const hm = (s) => s.slice(11, 16);
const endTime = (s, dur) => {
  const [h, m] = hm(s).split(":").map(Number);
  const t = h * 60 + m + dur;
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

function layoutDay(list) {
  const items = list
    .map((s) => {
      const [h, m] = hm(s.start).split(":").map(Number);
      const st = h * 60 + m;
      return { s, st, en: st + s.duration, lane: 0, lanes: 1 };
    })
    .sort((a, b) => a.st - b.st);
  const clusters = [];
  let laneEnds = [];
  let cluster = [];
  let clusterEnd = -1;
  for (const it of items) {
    if (cluster.length && it.st >= clusterEnd) {
      clusters.push(cluster);
      cluster = [];
      laneEnds = [];
    }
    let lane = laneEnds.findIndex((e) => e <= it.st);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(it.en);
    } else laneEnds[lane] = it.en;
    it.lane = lane;
    cluster.push(it);
    clusterEnd = Math.max(clusterEnd, it.en);
  }
  if (cluster.length) clusters.push(cluster);
  for (const cl of clusters) {
    const lanes = Math.max(...cl.map((i) => i.lane)) + 1;
    cl.forEach((i) => { i.lanes = lanes; });
  }
  return clusters.flat();
}

const selectCls = "border-b border-onyx/25 bg-transparent py-2 pr-6 font-sans text-sm text-onyx/75 focus:border-onyx focus:outline-none cursor-pointer";

function WeekView({ c, lang }) {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [sessions, setSessions] = useState([]);
  const [state, setState] = useState("loading");
  const [fActivity, setFActivity] = useState("");
  const [fCoach, setFCoach] = useState("");
  const [fLevel, setFLevel] = useState("");

  useEffect(() => {
    setState("loading");
    axios
      .get(`${API}/schedule`, { params: { start: iso(weekStart), end: iso(addDays(weekStart, 6)) } })
      .then((r) => { setSessions(r.data); setState("ok"); })
      .catch(() => setState("error"));
  }, [weekStart]);

  const activities = useMemo(() => [...new Set(sessions.map((s) => s.name))].sort(), [sessions]);
  const coaches = useMemo(() => [...new Set(sessions.map((s) => s.coach).filter(Boolean))].sort(), [sessions]);
  const levels = useMemo(() => [...new Set(sessions.map((s) => s.level).filter((l) => l != null))].sort(), [sessions]);

  const filtered = sessions.filter(
    (s) => (!fActivity || s.name === fActivity) && (!fCoach || s.coach === fCoach) && (!fLevel || String(s.level) === fLevel)
  );

  const isCurrentWeek = iso(weekStart) === iso(mondayOf(new Date()));
  const weekLabel = `${weekStart.getDate()} ${weekStart.toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { month: "short" })} – ${addDays(weekStart, 6).getDate()} ${addDays(weekStart, 6).toLocaleDateString(lang === "nl" ? "nl-NL" : "en-GB", { month: "short" })}`;
  const hours = Array.from({ length: (DAY_END - DAY_START) / 60 + 1 }, (_, i) => 7 + i);
  const gridH = (DAY_END - DAY_START) * PX_PER_MIN;

  const book = (s) => {
    track("book_class_click", { source: "schedule-week" });
    window.open(`https://backoffice.bsport.io/customer/payment/offer/${s.id}/?membership=6040&force=true`, "_blank", "noopener");
  };

  return (
    <div data-testid="schedule-week-view">
      {/* Week nav + filters */}
      <div className="flex flex-wrap items-center justify-between gap-6 border-t border-onyx/15 pt-6">
        <div className="flex items-center gap-6">
          <button
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            disabled={isCurrentWeek}
            className="label border border-onyx/25 px-4 py-2 transition-colors hover:border-onyx disabled:opacity-30"
            data-testid="week-prev"
            aria-label="Previous week"
          >
            ←
          </button>
          <p className="label text-onyx" data-testid="week-label">{isCurrentWeek ? `${c.thisWeek} · ` : ""}{weekLabel}</p>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} className="label border border-onyx/25 px-4 py-2 transition-colors hover:border-onyx" data-testid="week-next" aria-label="Next week">
            →
          </button>
          {!isCurrentWeek && (
            <button
              onClick={() => setWeekStart(mondayOf(new Date()))}
              className="label border-b border-onyx pb-1 transition-opacity hover:opacity-60"
              data-testid="week-today"
            >
              {c.today}
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-6">
          <select value={fActivity} onChange={(e) => setFActivity(e.target.value)} className={selectCls} data-testid="filter-activity">
            <option value="">{c.allActivities}</option>
            {activities.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select value={fLevel} onChange={(e) => setFLevel(e.target.value)} className={selectCls} data-testid="filter-level">
            <option value="">{c.allLevels}</option>
            {levels.map((l) => <option key={l} value={String(l)}>{c.level(l)}</option>)}
          </select>
          <select value={fCoach} onChange={(e) => setFCoach(e.target.value)} className={selectCls} data-testid="filter-instructor">
            <option value="">{c.allCoaches}</option>
            {coaches.map((co) => <option key={co} value={co}>{co}</option>)}
          </select>
          <select className={selectCls} data-testid="filter-location" defaultValue="">
            <option value="">{c.location}</option>
          </select>
        </div>
      </div>

      {state === "loading" && <p className="py-24 text-center font-serif text-2xl italic text-onyx/50">{c.loading}</p>}
      {state === "error" && (
        <div className="py-24 text-center">
          <p className="font-serif text-2xl italic text-onyx/60">{c.error}</p>
          <a href={BSPORT.SCHEDULE_URL} target="_blank" rel="noopener noreferrer" className="label mt-6 inline-block border-b border-onyx pb-1 hover:opacity-60">{c.openBsport}</a>
        </div>
      )}

      {state === "ok" && (
        <>
          {filtered.length === 0 && <p className="py-16 text-center font-sans text-base text-onyx/55" data-testid="week-empty">{c.empty}</p>}
          <div className="mt-8 flex">
            {/* time axis */}
            <div className="w-12 shrink-0 pt-12" style={{ height: gridH + 48 }}>
              {hours.map((h) => (
                <div key={h} className="relative" style={{ height: 60 * PX_PER_MIN }}>
                  <span className="absolute -top-2 right-2 font-sans text-[11px] text-onyx/40">{String(h).padStart(2, "0")}:00</span>
                </div>
              ))}
            </div>
            {/* day columns */}
            <div className="grid flex-1 grid-cols-7">
              {Array.from({ length: 7 }, (_, di) => {
                const day = addDays(weekStart, di);
                const dayIso = iso(day);
                const daySessions = filtered.filter((s) => s.start.slice(0, 10) === dayIso);
                const isToday = dayIso === iso(new Date());
                return (
                  <div key={dayIso} className="border-l border-onyx/10 last:border-r" data-testid={`week-col-${di}`}>
                    <div className={`flex h-12 items-baseline justify-center gap-2 border-b border-onyx/15 ${isToday ? "bg-blush/40" : ""}`}>
                      <span className="label text-onyx/60">{c.days[di]}</span>
                      <span className={`font-serif text-lg ${isToday ? "text-terracotta" : "text-onyx/80"}`}>{day.getDate()}</span>
                    </div>
                    <div className="relative" style={{ height: gridH }}>
                      {hours.slice(1).map((h, i) => (
                        <div key={h} className="absolute inset-x-0 border-t border-onyx/[0.06]" style={{ top: (i + 1) * 60 * PX_PER_MIN }} />
                      ))}
                      {layoutDay(daySessions).map(({ s, st, lane, lanes }) => {
                        const top = Math.max(0, (st - DAY_START) * PX_PER_MIN);
                        const height = Math.max(34, s.duration * PX_PER_MIN - 2);
                        return (
                          <button
                            key={s.id}
                            onClick={() => book(s)}
                            className={`group absolute overflow-hidden border p-1.5 text-left transition-colors duration-300 ${
                              s.full
                                ? "border-onyx/15 bg-petal text-onyx/40 hover:border-onyx/30"
                                : "border-onyx/20 bg-blush/35 hover:bg-onyx hover:text-petal"
                            }`}
                            style={{ top, height, left: `calc(${(lane / lanes) * 100}% + 3px)`, width: `calc(${100 / lanes}% - 6px)` }}
                            data-testid={`week-class-${s.id}`}
                            title={`${s.name} · ${hm(s.start)}–${endTime(s.start, s.duration)} · ${s.coach}`}
                          >
                            <p className="truncate font-sans text-[11px] font-medium leading-tight">{s.name}</p>
                            <p className="truncate font-sans text-[10px] opacity-70">{hm(s.start)}–{endTime(s.start, s.duration)}{s.coach ? ` · ${s.coach}` : ""}</p>
                            <p className={`truncate text-[9px] uppercase tracking-wider ${s.full ? "" : "text-clay group-hover:text-petal"}`}>
                              {s.full ? c.full : s.spots_left != null && s.spots_left > 0 && s.spots_left <= 3 ? c.left(s.spots_left) : c.available}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Schedule() {
  const { t, lang } = useLang();
  const c = WCOPY[lang] || WCOPY.en;
  const [view, setView] = useState("week");

  return (
    <>
      <PageHead label={t.schedule.label} title={t.schedule.title} lead={t.schedule.body} />

      <section className="bg-petal" data-testid="schedule-book">
        <div className="mx-auto max-w-[1600px] px-4 pb-16 pt-2 md:px-12 md:pb-24 md:pt-4">
          {/* View selector — desktop only; mobile always shows the list */}
          <div className="mb-8 hidden justify-end md:flex">
            <div className="inline-flex border border-onyx/25 p-1" data-testid="schedule-view-toggle">
              {[["week", c.week], ["list", c.list]].map(([key, lbl]) => (
                <button
                  key={key}
                  onClick={() => { setView(key); track("schedule_view_toggle", { view: key }); }}
                  className={`label px-8 py-3 transition-colors duration-300 ${view === key ? "bg-onyx text-petal" : "text-onyx/55 hover:text-onyx"}`}
                  data-testid={`schedule-view-${key}`}
                  aria-pressed={view === key}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {/* WEEK VIEW — desktop only */}
          {view === "week" && (
            <div className="hidden md:block">
              <WeekView c={c} lang={lang} />
            </div>
          )}

          {/* LIST VIEW (Bsport widget) — always on mobile, on desktop when selected */}
          <div className={view === "week" ? "md:hidden" : ""}>
            <div className="relative overflow-hidden border border-onyx/12 bg-petal p-2 md:p-6" data-testid="schedule-widget-container">
              <BsportWidget
                mountId="bsport-widget-calendar"
                widgetType="calendar"
                config={calendarConfig}
                className="relative z-10 min-h-[70vh] bg-petal"
                minHeight={620}
                fallback={
                  <div className="flex h-full min-h-[620px] flex-col items-center justify-center gap-5 px-6 text-center">
                    <SectionLabel>Live schedule · Bsport</SectionLabel>
                    <p className="max-w-md font-serif text-2xl italic leading-snug text-onyx/70 md:text-3xl">
                      Loading the live class schedule…
                    </p>
                    <p className="max-w-sm font-sans text-sm text-onyx/45">
                      If it doesn't appear,{" "}
                      <a
                        href={BSPORT.SCHEDULE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => track("view_schedule_click", { source: "schedule-fallback-link" })}
                        className="border-b border-onyx/40 transition-colors hover:border-onyx"
                      >
                        open it in a new window
                      </a>
                      .
                    </p>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-onyx text-petal" data-testid="schedule-new">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start justify-between gap-8 px-6 py-20 md:flex-row md:items-center md:px-12">
          <div>
            <SectionLabel light>{t.schedule.newTitle}</SectionLabel>
            <p className="mt-6 font-serif text-4xl md:text-5xl">{t.schedule.newBody}</p>
            <p className="mt-5 max-w-xl font-sans text-sm leading-relaxed text-petal/60" data-testid="schedule-intro-settle">{t.common.introSettle}</p>
          </div>
          <a href={BSPORT.INTRO_URL} target="_blank" rel="noopener noreferrer" onClick={() => track("intro_offer_click", { source: "schedule" })} className="label border border-petal px-10 py-5 transition-colors duration-500 hover:bg-petal hover:text-onyx" data-testid="schedule-intro-cta">
            {t.common.startIntro}
          </a>
        </div>
      </section>
    </>
  );
}
